// Shared traders-online hook. Moved here from features/dashboard so AppShell
// can mount the PresenceFab without creating a cross-feature import.
// The dashboard feature re-exports this via its own hooks/useTradersOnline.ts
// to keep its public API stable.
import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@/lib/query";
import { apiClient } from "@/lib/api/client";
import {
  readCachedTradersOnline,
  writeCachedTradersOnline,
} from "./traders-online-cache";

export type TradersOnlineCount = { count: number };

export async function fetchTradersOnlineShared(): Promise<TradersOnlineCount> {
  const result = await apiClient.GET("/api/v2/traders-online");
  const raw = (result.data ?? {}) as { count?: number };
  return { count: raw.count ?? 0 };
}

/** Query key lives here so FAB and dashboard share the same cache slot. */
export const tradersOnlineKey = ["shared", "traders-online"] as const;

/**
 * Returns the current traders-online count, polling every 60 s.
 *
 * @example
 * const { data } = useTradersOnline();
 * console.log(data?.count); // e.g. 42
 */
export function useTradersOnline(): UseQueryResult<TradersOnlineCount> {
  const cached = readCachedTradersOnline();
  const query = useQuery<TradersOnlineCount>({
    queryKey: tradersOnlineKey,
    queryFn: fetchTradersOnlineShared,
    refetchInterval: 60_000,
    staleTime: 30_000,
    ...(cached === null ? {} : { placeholderData: { count: cached } }),
  });
  useEffect(() => {
    if (typeof query.data?.count === "number") {
      writeCachedTradersOnline(query.data.count);
    }
  }, [query.data?.count]);
  return query;
}
